import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {PopoverPaper, Button, SearchBar} from '../';
import styles from './SelectionMenu.module.css';
import {Typography} from '@mui/material';

const SelectionMenu = ({
	open,
	handleClose,
	anchorEl,
	options,
	currentSelections,
	resetToDefault,
	applySelections,
	defaultColumns,
}) => {
	const [columnSelections, setColumnSelections] = useState([
		...currentSelections,
	]);

	const selectAllCheckboxRef = useCallback(
		node => {
			if (node !== null) {
				if (
					columnSelections.length &&
					columnSelections.length === options.length
				) {
					node.checked = true;
					node.indeterminate = false;
				} else if (columnSelections.length > 0) {
					node.indeterminate = true;
					node.checked = false;
				} else {
					node.checked = false;
					node.indeterminate = false;
				}
			}
		},
		[columnSelections, options],
	);

	const handleColumnSelectionChange = option => {
		if (columnSelections.includes(option)) {
			setColumnSelections(prevState => prevState.filter(col => col !== option));
		} else {
			setColumnSelections(prevState => [...prevState, option]);
		}
	};

	const handleSelectAllClick = () => {
		if (columnSelections.length === options.length) {
			setColumnSelections([]);
		} else {
			setColumnSelections([...options]);
		}
	};

	const applyResetDefault = () => {
		setColumnSelections(defaultColumns);
		resetToDefault();
	};

	useEffect(() => {
		if (currentSelections) setColumnSelections([...currentSelections]);
	}, [currentSelections]);

	const [searchQuery, setSearchQuery] = useState('');

	const filterOptionsFunc = (optionsToBeFiltered, query) => {
		if (!query) {
			return optionsToBeFiltered;
		}
		const searchedOption = optionsToBeFiltered.filter(option => {
			const optionName = option.toLowerCase();
			const lowerCaseQuery = query.toLowerCase();
			return optionName.includes(lowerCaseQuery);
		});
		return searchedOption;
	};
	const filteredOptions = filterOptionsFunc(options, searchQuery);

	return (
		<PopoverPaper
			open={open}
			handleClose={() => {
				handleClose();
				setSearchQuery('');
			}}
			anchorEl={anchorEl}
			anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
			transformOrigin={{vertical: 'top', horizontal: 'center'}}
		>
			<div className={styles.selectionMenuContainer}>
				<Button
					onClick={() => applyResetDefault()}
					variant='secondary'
					className={styles.resetToDefaultButton}
				>
					Reset
				</Button>
				<SearchBar value={searchQuery} onChange={setSearchQuery} />
				<div className={clsx(styles.selectionMenu, styles.scrollBar)}>
					{filteredOptions?.length > 1 && (
						<div className={styles.selectOption}>
							<input
								ref={selectAllCheckboxRef}
								name='select-all'
								type='checkbox'
								data-testid='select-all-filters'
								onChange={handleSelectAllClick}
							/>
							<Typography htmlFor='select-all-filters'>Select All</Typography>
						</div>
					)}
					{filteredOptions.map(option => (
						<div key={option}>
							{option && (
								<div className={styles.selectOption}>
									<input
										data-testid={`column-selection-for-${option}`}
										id={`column-selection-for-${option}`}
										type='checkbox'
										name={option}
										onChange={() => handleColumnSelectionChange(option)}
										checked={columnSelections.includes(option)}
									/>
									<Typography htmlFor='option'>{option}</Typography>
								</div>
							)}
						</div>
					))}
				</div>
				<div className={styles.buttonsWrapper}>
					<Button
						onClick={() => {
							setColumnSelections([...currentSelections]);
							handleClose();
							setSearchQuery('');
						}}
						variant='secondary'
						className={styles.cancelButton}
					>
						Cancel
					</Button>
					<Button
						onClick={() => {
							applySelections(columnSelections);
							handleClose();
							setSearchQuery('');
						}}
						className={styles.okButton}
					>
						OK
					</Button>
				</div>
			</div>
		</PopoverPaper>
	);
};

SelectionMenu.propTypes = {
	open: PropTypes.bool.isRequired,
	handleClose: PropTypes.func.isRequired,
	anchorEl: PropTypes.instanceOf(Element),
	options: PropTypes.arrayOf(PropTypes.string).isRequired,
	currentSelections: PropTypes.arrayOf(PropTypes.string).isRequired,
	resetToDefault: PropTypes.func.isRequired,
	applySelections: PropTypes.func.isRequired,
};

export default SelectionMenu;
